import { useState, useEffect, useCallback, UIEvent } from "react";
import { Button, IconButton } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";

import "../file-table/style.scss";
import { ReactComponent as TivaPlaceholder } from "../../../assets/placeholder-tiva.svg";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/deleteIcon.svg";
import {
    LearningObjectStatus,
    TrialLearningObjectStatus
} from "../../../common/enums/learning-object-status.enum";
import { LearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import {
    RecognitionResultStatus,
    RRSwitchStatus
} from "../../../common/enums/recognition-result-status.enum";
import { Loader } from "../../loader/loader.comp";
import { SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";

const TrialLearningObjects = ({
    totalCount,
    learningObjects,
    checkedLOs,
    setCheckedLOs,
    handleAppendLOs,
    handleDelete,
    handleAIResult,
    handleChangePublishStatus
}: {
    totalCount?: number;
    learningObjects: LearningObject[];
    checkedLOs: LearningObject[];
    setCheckedLOs: (arg1: LearningObject[]) => void;
    handleAppendLOs?: () => void;
    handleDelete?: (arg1: string[]) => void;
    handleAIResult?: (arg1: string) => void;
    handleChangePublishStatus: (arg1: RecognitionResult, arg2: string) => void;
}) => {
    const scrollDelata = 250;
    const [appendAvailable, setAppendAvailable] = useState<boolean>(true);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [rrStatusList] = useState<string[]>(Object.values(RRSwitchStatus));

    useEffect(() => {
        setShowLoader(!!handleAppendLOs && !!totalCount && totalCount !== learningObjects.length);
    }, [learningObjects, totalCount, handleAppendLOs]);

    const handleChangeSelectAll = useCallback(() => {
        setCheckedLOs(learningObjects.length === checkedLOs.length ? [] : learningObjects);
    }, [learningObjects, checkedLOs, setCheckedLOs]);

    const handleChangeSelect = useCallback(
        (item: LearningObject) => {
            const newFiles = [...checkedLOs];
            const index = newFiles.indexOf(item);
            index !== -1 ? newFiles.splice(index, 1) : newFiles.push(item);
            setCheckedLOs(newFiles);
        },
        [checkedLOs, setCheckedLOs]
    );

    const handleAIResultClick = (item: LearningObject) => {
        if (handleAIResult && item.recognitionResults.length) {
            handleAIResult(item.recognitionResults[0].uid);
        }
    };

    const handleDeleteClick = (item: LearningObject) => {
        if (handleDelete) {
            handleDelete([item.loItemId]);
        }
    };

    const fileStatusColor = (status: string) => {
        switch (status as LearningObjectStatus) {
            case LearningObjectStatus.active:
            case LearningObjectStatus.inUse:
                return "green";
            case LearningObjectStatus.idle:
            case LearningObjectStatus.recognizing:
            case LearningObjectStatus.transcribing:
            case LearningObjectStatus.waitingForDownload:
            case LearningObjectStatus.waitingForRecognition:
                return "yellow";
            case LearningObjectStatus.error:
                return "red";
            default:
                return "default";
        }
    };

    const fileStatusName = (status: string) => {
        switch (status as LearningObjectStatus) {
            case LearningObjectStatus.active:
            case LearningObjectStatus.inUse:
                return TrialLearningObjectStatus.ready;
            case LearningObjectStatus.idle:
                return TrialLearningObjectStatus.idle;
            case LearningObjectStatus.recognizing:
            case LearningObjectStatus.transcribing:
            case LearningObjectStatus.waitingForDownload:
            case LearningObjectStatus.waitingForRecognition:
                return TrialLearningObjectStatus.waiting;
            case LearningObjectStatus.error:
                return TrialLearningObjectStatus.error;
            default:
                return status;
        }
    };

    const isAIResultAvailable = (item: LearningObject) => {
        if (!item.recognitionResults.length) return false;

        const status = item.recognitionResults[0].status;

        return (
            status === RecognitionResultStatus.active ||
            status === RecognitionResultStatus.published ||
            status === RecognitionResultStatus.recognized ||
            status === RecognitionResultStatus.generatingSummary ||
            status === RecognitionResultStatus.waitingForSummary
        );
    };

    const getCurrentRR = (item: LearningObject) => {
        if (!item.recognitionResults.length) return null;

        return item.recognitionResults.find(rr => rr.isPublish) ?? item.recognitionResults[0];
    };

    const changePublishStatus = (status: string, loName: string, rr: RecognitionResult | null) => {
        if (!rr) return;

        if (rr.isPublish !== (status === RRSwitchStatus.published)) {
            handleChangePublishStatus(rr, loName);
        }
    };

    const handleListScroll = (event: UIEvent<HTMLElement>) => {
        if (!handleAppendLOs || (totalCount && totalCount === learningObjects.length)) return;

        const scrollBottom =
            event.currentTarget.scrollHeight -
            (event.currentTarget.scrollTop + event.currentTarget.offsetHeight);

        if (appendAvailable && scrollBottom < scrollDelata) {
            setAppendAvailable(false);
            handleAppendLOs();
        } else if (scrollBottom >= scrollDelata) {
            setAppendAvailable(true);
        }
    };

    return (
        <div className="file-table-view">
            {learningObjects.length > 0 ? (
                <div className="file-table">
                    <div className="file-table-header">
                        <div className="file-table-header-cell file-table-cell__select">
                            <Checkbox
                                checked={learningObjects.length === checkedLOs.length}
                                onChange={handleChangeSelectAll}
                                icon={<CheckboxIcon />}
                                checkedIcon={<CheckboxCheckedIcon />}
                            />
                        </div>
                        <div className="file-table-header-cell file-table-cell__name">
                            File Name
                        </div>
                        <div className="file-table-header-cell file-table-cell__status">Status</div>
                        <div className="file-table-header-cell file-table-cell__rr-status">
                            RR Status
                        </div>
                        <div className="file-table-header-cell file-table-cell__result"></div>
                        <div className="file-table-header-cell file-table-cell__delete"></div>
                    </div>
                    <div className="file-table-body" onScroll={handleListScroll}>
                        {learningObjects.map(item => (
                            <div key={item.loItemId} className="file-table-row">
                                <div className="file-table-cell file-table-cell__select">
                                    <Checkbox
                                        checked={checkedLOs.includes(item)}
                                        onChange={() => handleChangeSelect(item)}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                    />
                                </div>
                                <div className="file-table-cell file-table-cell__name">
                                    <span className="table-file-name">{item.name}</span>
                                </div>
                                <div className="file-table-cell file-table-cell__status">
                                    <div
                                        className={`table-file-status table-file-status__${fileStatusColor(item.status)}`}
                                    >
                                        <div className="table-file-status__circle"></div>
                                        <span className="table-file-status__title">
                                            {fileStatusName(item.status)}
                                        </span>
                                    </div>
                                </div>
                                <div className="file-table-cell file-table-cell__rr-status">
                                    <SelectionFilter
                                        list={rrStatusList}
                                        disabled={!item.recognitionResults.length}
                                        selectedItem={
                                            getCurrentRR(item)?.isPublish
                                                ? RRSwitchStatus.published
                                                : RRSwitchStatus.off
                                        }
                                        setSelectedItem={status =>
                                            changePublishStatus(
                                                status,
                                                item.name,
                                                getCurrentRR(item)
                                            )
                                        }
                                        rightPosition={true}
                                    />
                                </div>
                                <div className="file-table-cell file-table-cell__result">
                                    <Button
                                        className="table-file__button-result"
                                        variant="outlined"
                                        disabled={!isAIResultAvailable(item)}
                                        onClick={() => handleAIResultClick(item)}
                                    >
                                        {isAIResultAvailable(item) ? "AI Result" : "Loading"}
                                    </Button>
                                </div>
                                <div className="file-table-cell file-table-cell__delete">
                                    <IconButton
                                        className="table-file__button-delete"
                                        onClick={() => handleDeleteClick(item)}
                                    >
                                        <SvgIcon component={DeleteIcon} inheritViewBox />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                        {showLoader && (
                            <div className="file-table-row file-table-row__loader">
                                <Loader size={24} borderWidth={4} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="file-table-placeholder">
                    <SvgIcon
                        component={TivaPlaceholder}
                        className="file-table-placeholder-logo"
                        inheritViewBox
                    />
                </div>
            )}
        </div>
    );
};

export default TrialLearningObjects;
